<template>
  <div class="min-h-screen bg-bochas-bg">
    <!-- Hero (estilo home: navbar transparente sobre el hero, fondo llega a top:0) -->
    <ImageBackgroundSection
      size="large"
      align="center"
      seed="jóvenes-hero"
      :image-url="heroImageUrl"
      :loading-image="heroImageLoading"
      alt="Jóvenes Bochófilos"
      top-icon="jovenPromesa"
      top-label="El futuro"
      :title="heroMedia?.title || 'Jóvenes Bochófilos'"
      :subtitle="
        heroMedia?.subtitle || 'Los talentos emergentes de la bocha cordobesa'
      "
    />

    <!-- Loading -->
    <section v-if="loading" class="max-w-7xl mx-auto px-4 py-12 text-center">
      <div
        class="inline-block w-10 h-10 sm:w-12 sm:h-12 border-4 border-bochas-rojo border-t-transparent rounded-full animate-spin"
      ></div>
    </section>

    <!-- Error -->
    <section v-else-if="error" class="max-w-7xl mx-auto px-4 py-12">
      <div
        class="bg-bochas-rojo/20 border border-bochas-rojo text-red-200 rounded-lg p-4"
      >
        {{ error }}
      </div>
    </section>

    <!-- Empty -->
    <section
      v-else-if="jóvenes.length === 0"
      class="max-w-7xl mx-auto px-4 py-12 text-center"
    >
      <p class="text-gray-400 text-base sm:text-lg">
        Aún no hay jóvenes bochófilos publicados.
      </p>
    </section>

    <!-- Listado -->
    <section
      v-else
      class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-24"
    >
      <div
        class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6"
      >
        <RevealOnScroll
          v-for="(j, index) in jóvenes"
          :key="j.id"
          as="article"
          :delay="(index % 3) * 100"
          class="card-fbochas overflow-hidden flex flex-col transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-bochas-rojo/30 active:scale-[0.98]"
        >
          <router-link
            :to="`/jóvenes-promesas/${j.id}?from=jóvenes`"
            class="flex flex-col h-full"
          >
            <div class="h-[300px] lg:h-[250px] overflow-hidden bg-bochas-bg">
              <img
                v-if="j.photo_url"
                :src="j.photo_url"
                :alt="j.name"
                class="listing-motion-image w-full h-full object-cover"
                loading="lazy"
              />
              <div
                v-else
                class="w-full h-full bg-degradado-bochas flex items-center justify-center"
              >
                <span class="text-white text-4xl sm:text-5xl font-bold">
                  {{ initials(j) }}
                </span>
              </div>
            </div>

            <div class="p-4 sm:p-5 flex-1 flex flex-col text-center">
              <h2
                class="text-lg sm:text-xl font-bold text-white mb-1 line-clamp-2"
              >
                {{ j.name }}
              </h2>
              <p
                v-if="j.association"
                class="text-xs sm:text-sm text-bochas-text-muted mb-2"
              >
                {{ j.association }}
              </p>
              <p
                v-if="j.careerStartDate || j.careerEndDate || j.yearsPlaying > 0"
                class="text-xs sm:text-sm text-bochas-rojo mb-2"
              >
                {{ careerPeriod(j, `${j.yearsPlaying} años jugando`) }}
              </p>
              <p
                v-if="j.shortBio || j.bio"
                class="text-xs sm:text-sm text-gray-300 line-clamp-2 flex-1"
              >
                {{ textPreview(j.shortBio || j.bio) }}
              </p>
              <span
                class="mt-3 text-bochas-rojo text-xs sm:text-sm font-semibold"
              >
                Conocer más →
              </span>
            </div>
          </router-link>
        </RevealOnScroll>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { listarJovenes } from "@/services/youngPlayers/youngPlayersService";
import ImageBackgroundSection from "@/components/Common/ImageBackgroundSection.vue";
import RevealOnScroll from "@/components/Animations/RevealOnScroll.vue";
import { useSiteMediaImageState } from "@/composables/useSiteMediaImage";
import { careerPeriod } from "@/utils/playerCareer";

const jóvenes = ref([]);
const loading = ref(true);
const error = ref("");
const {
  imageUrl: heroImageUrl,
  item: heroMedia,
  loading: heroImageLoading,
} = useSiteMediaImageState("futuro.hero");

async function load() {
  loading.value = true;
  error.value = "";
  try {
    const data = await listarJovenes();
    jóvenes.value = data.items || data || [];
  } catch (err) {
    error.value =
      err.response?.data?.message ||
      "No pudimos cargar los jóvenes bochófilos.";
  } finally {
    loading.value = false;
  }
}

function initials(j) {
  if (!j.name) return "";
  return j.name
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();
}

function textPreview(text) {
  if (!text) return "";
  const trimmed = text.replace(/<[^>]*>/g, "").trim();
  return trimmed.length > 120 ? trimmed.slice(0, 120) + "…" : trimmed;
}

onMounted(load);
</script>
